import React from "react"
import { FaCheckCircle, FaExclamationTriangle, FaExclamationCircle } from "react-icons/fa"

/**
 * Calculate staffing status based on current vs recommended staffing
 * @param {number} currentStaffingCount - Current number of nurses scheduled
 * @param {number} recommendedNursesCount - Recommended number of nurses
 * @returns {object} Status object with color, background, text, and icon
 */
export const getStaffingStatus = (currentStaffingCount, recommendedNursesCount) => {
	const difference = currentStaffingCount - recommendedNursesCount
	const percentageDiff =
		recommendedNursesCount > 0 ? (difference / recommendedNursesCount) * 100 : 100

	if (Math.abs(percentageDiff) <= 10) {
		return {
			status: "good",
			color: "text-green-600",
			bgColor: "bg-green-100",
			text: "Well Staffed",
			icon: <FaCheckCircle />
		}
	} else if (difference < 0) {
		return {
			status: "understaffed",
			color: "text-red-600",
			bgColor: "bg-red-100",
			text: `Understaffed by ${Math.abs(difference)}`,
			icon: <FaExclamationTriangle />
		}
	} else {
		return {
			status: "overstaffed",
			color: "text-yellow-600",
			bgColor: "bg-yellow-100",
			text: `Overstaffed by ${difference}`,
			icon: <FaExclamationCircle />
		}
	}
}

/**
 * StaffingStatusBadge component for displaying staffing status
 * @param {number} currentStaffingCount - Current number of nurses scheduled
 * @param {number} recommendedNursesCount - Recommended number of nurses
 * @param {Array} currentNurseSchedule - Array of current nurse schedule data
 * @param {string} size - Size variant: 'sm' for compact, 'md' for normal
 * @param {string} className - Additional CSS classes
 */
const StaffingStatusBadge = ({
	currentStaffingCount,
	recommendedNursesCount,
	currentNurseSchedule = [],
	size = "md",
	className = ""
}) => {
	// Check if there are any default nurses in the schedule
	const hasDefaultNurses = currentNurseSchedule.some(nurse =>
		nurse.nurseName?.toUpperCase().includes("DEFAULT")
	)

	// If there are default nurses, show "Need to enter staffing" message
	if (hasDefaultNurses) {
		const defaultStatus = {
			status: "default",
			color: "text-orange-600",
			bgColor: "bg-orange-100",
			text: "Need to enter staffing",
			icon: <FaExclamationTriangle />
		}

		const sizeClasses = {
			sm: "px-2 py-0.5 text-xs",
			md: "px-3 py-1 text-sm"
		}

		const iconSizeClasses = {
			sm: "text-xs",
			md: "text-sm"
		}

		return (
			<div
				className={`${sizeClasses[size]} rounded-full font-medium flex items-center gap-2 ${defaultStatus.bgColor} ${defaultStatus.color} ${className}`}
			>
				{React.cloneElement(defaultStatus.icon, { className: iconSizeClasses[size] })}
				<span>{defaultStatus.text}</span>
			</div>
		)
	}

	// Otherwise, use the normal staffing status logic
	const staffingStatus = getStaffingStatus(currentStaffingCount, recommendedNursesCount)

	const sizeClasses = {
		sm: "px-2 py-0.5 text-xs",
		md: "px-3 py-1 text-sm"
	}

	const iconSizeClasses = {
		sm: "text-xs",
		md: "text-sm"
	}

	return (
		<div
			className={`${sizeClasses[size]} rounded-full font-medium flex items-center gap-2 ${staffingStatus.bgColor} ${staffingStatus.color} ${className}`}
		>
			{React.cloneElement(staffingStatus.icon, { className: iconSizeClasses[size] })}
			<span>{staffingStatus.text}</span>
		</div>
	)
}

export default StaffingStatusBadge
